import { createUniqueId, type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import type { CapSwitchView } from '../../lib/machine-capabilities';

// One capability switch on the Mission Panel card (#6766, prototype capGrid, `:2296-2303`): a
// real `<button role="switch" aria-checked>`, so Space and Enter are native activation.
// CONTROLLED: a click asks the card's controller (`lib/capability-switches.ts`) and every
// attribute follows the view (`lib/machine-capabilities.ts`). Its name is the role's, plus
// ", running", ", finishing then off" and ", saving" while they hold; its `title` the role's
// caption, or "Keep at least one capability on" on the last role on. Busy or locked it is
// `aria-disabled` (still focusable, clicks ignored); offline or the last role on, `disabled`.
// A blocked role's reason and a refusal's sentence sit UNDER the button, described by it.

export function CapabilitySwitch(props: {
  readonly sw: CapSwitchView;
  readonly onToggle: () => void;
}): JSX.Element {
  const blockedId = createUniqueId();
  const refusalId = createUniqueId();
  const name = (): string => t(props.sw.nameKey);
  const saving = (): boolean => props.sw.pending || props.sw.busy;
  const label = (): string =>
    name() +
    (props.sw.running ? t('fleet.card.caps.running_suffix') : '') +
    (props.sw.finishing ? t('fleet.card.caps.finishing_suffix') : '') +
    (saving() ? t('fleet.card.caps.saving_suffix') : '');
  const describedBy = (): string | undefined => {
    const ids = [
      props.sw.blockedKey === null ? null : blockedId,
      props.sw.refusal === null ? null : refusalId,
    ].filter((x): x is string => x !== null);
    return ids.length === 0 ? undefined : ids.join(' ');
  };
  return (
    <div class="mission-sw" data-cap={props.sw.key}>
      <button
        type="button"
        role="switch"
        class="mission-sw__btn"
        classList={{ 'mission-sw__btn--fin': props.sw.finishing }}
        aria-checked={props.sw.checked}
        aria-label={label()}
        aria-describedby={describedBy()}
        aria-disabled={props.sw.hold ? 'true' : undefined}
        aria-busy={props.sw.busy ? 'true' : undefined}
        title={props.sw.lastOn ? t('fleet.card.caps.last_on') : t(props.sw.captionKey)}
        disabled={props.sw.disabled}
        onClick={() => props.onToggle()}
      >
        <span class="mission-sw__track" aria-hidden="true">
          <span class="mission-sw__knob" />
        </span>
        <span class="mission-sw__name">{name()}</span>
        <Show when={props.sw.running}>
          <span class="mission-sw__dot--live" aria-hidden="true" />
        </Show>
        <Show when={props.sw.finishing}>
          <span class="mission-sw__fin">{t('fleet.card.caps.then_off')}</span>
        </Show>
        <Show when={saving()}>
          <span class="mission-sw__status">{t('fleet.card.caps.saving')}</span>
        </Show>
        <Show when={props.sw.saved}>
          <span class="mission-sw__status mission-sw__status--saved">
            {t('fleet.card.caps.saved')}
          </span>
        </Show>
      </button>
      <Show when={props.sw.blockedKey}>
        {(key) => (
          <p id={blockedId} class="mission-sw__why">
            {t(key())}
          </p>
        )}
      </Show>
      <Show when={props.sw.refusal}>
        {(text) => (
          <p id={refusalId} class="mission-sw__refusal">
            {text()}
          </p>
        )}
      </Show>
    </div>
  );
}
